/**
 * Settings › Notifications (collaboration phase 4): per event, in Flow, in Flow and by email, or off; how emails go
 * (right away, a daily digest, or none); and per project, everything, only mentions, or muted. Kept by the sandbox
 * gateway with the person's email preferences (`PUT /api/v1/me/notifications { notify }`, a partial change) and
 * enforced there, so they hold on every device.
 *
 * Props: `settings` (`{ shareEmails, available, notify }` from getNotificationSettings), `onChange(partialNotify)`
 * (saves; the parent shows an error), `projects` (`[{ id, name }]` the person may set per project).
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Segmented, Select } from 'antd';

export const EVENT_ROWS = [
  { key: 'mention', label: 'Mentions and replies', hint: 'Someone @mentions you in a comment.' },
  { key: 'share', label: 'Projects shared with me', hint: 'Someone shares a project with you, or makes you an editor.' },
  { key: 'suggestion', label: 'Suggestions', hint: 'An editor answers your suggestion.' },
  { key: 'handoff', label: 'Handoff notes for me', hint: 'Someone leaves you a note on what they did and what is next.' },
  { key: 'note', label: 'Notes from AI agents and people', hint: 'An AI agent or a person leaves a note for you.' },
  { key: 'conflict', label: 'Someone changes lines I am editing', hint: 'An agent or a person writes over lines you were typing.' },
  { key: 'agentDone', label: 'An agent I watch is done', hint: 'After “Notify me when it’s done”.' },
  { key: 'joined', label: 'Someone joins a project I own', hint: 'Only their first visit of the day.' },
];

const CHOICES = [
  { value: 'inapp', label: 'In Flow' },
  { value: 'email', label: 'Email too' },
  { value: 'off', label: 'Off' },
];
const EMAIL_MODES = [
  { value: 'now', label: 'Right away' },
  { value: 'digest', label: 'Daily digest at 9:00' },
  { value: 'off', label: 'Off' },
];
const LEVELS = [
  { value: 'all', label: 'All' },
  { value: 'mentions', label: 'Mentions only' },
  { value: 'muted', label: 'Muted' },
];

export default function NotificationSettings({ settings, onChange, projects = [] }) {
  const [adding, setAdding] = useState(null);
  const notify = settings?.notify;
  if (!notify) { return null; }
  const emailsOff = !settings.shareEmails || !settings.available;
  const named = (id) => projects.find((p) => p.id === id)?.name || id;
  const set = Object.entries(notify.projects || {});
  const choosable = projects.filter((p) => !(notify.projects || {})[p.id]);

  return (
    <section aria-labelledby="set-notify" className="sbx-card settings-section" data-testid="notification-settings">
      <h2 id="set-notify">Notifications</h2>
      <div className="settings-row settings-row-stack">
        <span className="settings-label">
          <strong>Notify me</strong>
          <span>In Flow: the bell. Email too: also by email, as chosen below. Someone simply opening a file, cursor moves and an agent’s single writes never notify: they stay in presence and Activity.</span>
        </span>
        <div className="settings-notify-table" role="table" aria-label="Notifications per event">
          {EVENT_ROWS.map((row) => (
            <div className="settings-notify-row" key={row.key} role="row">
              <span className="settings-notify-event" role="rowheader">
                <strong>{row.label}</strong>
                <span>{row.hint}</span>
              </span>
              <span role="cell">
                <Segmented
                  aria-label={row.label}
                  onChange={(value) => onChange({ events: { [row.key]: value } })}
                  options={CHOICES}
                  size="small"
                  value={notify.events?.[row.key] || 'inapp'}
                />
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="settings-row">
        <span className="settings-label">
          <strong>Emails</strong>
          <span>
            {emailsOff
              ? (settings.available ? 'Sharing emails are off (above): no notification emails either.' : 'Your organization has not turned on emails yet: your choice is kept for when it does.')
              : 'The daily digest collects what you chose to get by email, unread, once a day. Invitations to a project always come right away.'}
          </span>
        </span>
        <Segmented aria-label="How emails come" onChange={(value) => onChange({ email: value })} options={EMAIL_MODES} value={notify.email || 'now'} />
      </div>
      <div className="settings-row settings-row-stack">
        <span className="settings-label">
          <strong>Per project</strong>
          <span>A project can tell you everything, only mentions, or nothing (muted).</span>
        </span>
        {set.map(([id, level]) => (
          <div className="settings-notify-row" data-testid="project-notify" key={id}>
            <span className="settings-notify-event"><strong>{named(id)}</strong></span>
            <Segmented aria-label={`Notifications for ${named(id)}`} onChange={(value) => onChange({ projects: { [id]: value } })} options={LEVELS} size="small" value={level} />
          </div>
        ))}
        {choosable.length > 0 && (
          <div className="settings-notify-add">
            <Select
              aria-label="A project to set"
              onChange={(value) => { setAdding(null); onChange({ projects: { [value]: 'mentions' } }); }}
              options={choosable.map((p) => ({ value: p.id, label: p.name || p.id }))}
              placeholder="Set a project…"
              showSearch
              style={{ minWidth: 240 }}
              value={adding}
            />
          </div>
        )}
      </div>
    </section>
  );
}

NotificationSettings.propTypes = {
  onChange: PropTypes.func.isRequired,
  projects: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
  settings: PropTypes.object,
};
